import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, Table } from 'reactstrap';
import ArrowRightIcon from 'mdi-react/ArrowRightIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';

const STATUS_COLORS = {
  Active: 'success',
  New: 'info',
  'Pending Activation': 'info',
  'Pending Deactivation': 'warning',
  Deactivated: 'danger',
  Deleted: 'danger',
};

// Anything else the sync reports is the error it recorded for the resident.
const RESULT_COLORS = {
  processed: 'success',
  'no change': 'secondary',
  'not invited': 'warning',
  'not processed': 'danger',
};
const STARTING_POINT_PREFIX = 'starting point saved';

const PROFILE_LABELS = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  unit_number: 'Unit',
};

const Result = ({ result }) => {
  if (!result.startsWith(STARTING_POINT_PREFIX)) {
    return <Badge color={resultColor(result)} className='text-wrap text-left'>{result}</Badge>;
  }

  return (
    <>
      <Badge color={resultColor(result)}>{STARTING_POINT_PREFIX}</Badge>
      <small className='text-muted d-block mt-1'>Change the file and run again.</small>
    </>
  );
};

const resultColor = result => (result.startsWith(STARTING_POINT_PREFIX) ? 'primary' : RESULT_COLORS[result] || 'danger');

const StatusBadge = ({ status }) => <Badge color={STATUS_COLORS[status] || 'secondary'}>{status}</Badge>;

const Status = ({ before, after }) => {
  if (!before && !after) {
    return '-';
  }
  if (before === after || !before || !after) {
    return <StatusBadge status={after || before} />;
  }

  return (
    <span className='text-nowrap'>
      <StatusBadge status={before} /> <ArrowRightIcon size={18} /> <StatusBadge status={after} />
    </span>
  );
};

const removedStyle = { textDecoration: 'line-through', color: '#c33' };

const Programs = ({ before, after }) => {
  const added = after.filter(name => !before.includes(name));
  if (!before.length && !added.length) {
    return '-';
  }

  return (
    <>
      {before.map(name => (
        <div key={name} style={after.includes(name) ? undefined : removedStyle}>{name}</div>
      ))}
      {added.map(name => <div key={name} className='text-success'>+ {name}</div>)}
    </>
  );
};

const ProfileChanges = ({ changes }) => {
  if (!changes.length) {
    return <span className='text-muted'>none</span>;
  }

  return changes.map(change => (
    <div key={change.field} className='text-nowrap'>
      <small className='text-muted d-block'>{PROFILE_LABELS[change.field] || change.field}</small>
      <span style={removedStyle}>{change.before || '(empty)'}</span>
      {' '}<ArrowRightIcon size={18} />{' '}
      <span>{change.after || '(empty)'}</span>
    </div>
  ));
};

const summaryStyle = { fontSize: '0.8rem' };

const Summary = ({ residents }) => {
  const counts = residents.reduce((totals, { result }) => {
    const label = result.startsWith(STARTING_POINT_PREFIX) ? STARTING_POINT_PREFIX : result;
    return { ...totals, [label]: { count: (totals[label]?.count || 0) + 1, color: resultColor(result) } };
  }, {});

  return (
    <div className='mb-3'>
      {Object.entries(counts).map(([label, { count, color }]) => (
        <Badge key={label} color={color} pill className='mr-2 px-3 py-2' style={summaryStyle}>{`${count} ${label}`}</Badge>
      ))}
    </div>
  );
};

const EntrataTestRunReport = ({ residents }) => (
  <>
    <Summary residents={residents} />
    <Table responsive hover className='table--bordered'>
      <thead>
        <tr>
          <th>Resident</th>
          <th>User</th>
          <th>Status</th>
          <th>Participant in</th>
          <th>Profile changes</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        {residents.map(resident => (
          <tr key={resident.customer_id}>
            <td>
              <div className='bold-text'>{resident.name}</div>
              <small className='text-muted'>{`${resident.customer_id} · ${resident.email}`}</small>
            </td>
            <td>
              {resident.user_id
                ? (
                  <Link to={`/users/view/${resident.user_id}`} target='_blank' rel='noopener noreferrer' className='text-nowrap'>
                    {resident.user_id}
                    <OpenInNewIcon size={16} className='ml-1' />
                  </Link>
                )
                : '-'}
            </td>
            <td className='pr-4'><Status before={resident.status_before} after={resident.status_after} /></td>
            <td><Programs before={resident.participant_programs_before} after={resident.participant_programs_after} /></td>
            <td><ProfileChanges changes={resident.profile_changes} /></td>
            <td><Result result={resident.result} /></td>
          </tr>
        ))}
      </tbody>
    </Table>
  </>
);

export default EntrataTestRunReport;
